import { defineStore, type Store } from "pinia";
import {
  fromUnixToAPIDate,
  getEndDate,
  getStartDate,
  toExternalDateRange,
  toLongDateRange,
  toUnix,
} from "@/stories/utils/dates";
import joi from "joi";
import { useAuthStore } from "@/stores/auth.module";
import md5 from "md5";
import type {
  IDefaultState,
  IDefaultStoreActions,
  IDefultStoreGetters,
  IFileState,
  IImage,
  IOption,
} from "@/interfaces";
import type {
  EditRepeatTypes,
  RruleOptions,
  StoreName,
  TabErrorSchemaTypes,
  EditorModel,
  IdType,
  RRuleSupportedEntityType,
  ListResourceType,
  DefaultFilterType,
  VisibleStateTypes,
} from "@/types";
import helpers from "@/helpers";
import { fixValidationFields, validate } from "@/helpers/forms/validate";
import { globali18n } from "@/i18n";
import {
  type Video,
  type NewsInput,
  Group_Connection,
  Module_Type,
  type NewsUnion,
  type CompactInstitutionFragment,
  type EntityPagerFilter,
  Sort_Field,
  Sort_Order,
} from "@/graphql/generated-types";
import toImageList from "@/helpers/files/toImageList";
import api from "@/api";
import PagerManager from "@/helpers/manager/PagerManager";
import toRRule from "@/helpers/rrules/toRRule";
import { RRule, RRuleSet, rrulestr } from "rrule";
import fromRRuletoRuleState from "@/helpers/rrules/fromRRuletoRuleState";
import { IS_IBG_MANAGER, REPEAT_RULE_OPTIONS } from "@/consts";
import type { SummarizedNews } from "@/graphql";
import { useDepartments } from "@/composables/useDepartments";
import { useExternalDepartments } from "@/composables/useExternalDepartments";
import { useGroups } from "@/composables/useGroups";
import { useExternalGroups } from "@/composables/useExternalGroups";
import whenComposablesReady from "@/helpers/requests/whenComposablesReady";
import converters from "@/converters";
import getMaxDescriptionLength from "@/helpers/environment/getMaxDescriptionLength";
import { useEntityVisibility } from "@/composables/useEntityVisibility";

const VALIDATION_SCHEMA = joi.object({
  title: joi.string().max(50).required(),
  description: joi.string().max(getMaxDescriptionLength()).required(),
  startDate: joi.number().required(),
  endDate: joi.number().required().greater(joi.ref("startDate")),
  image: joi.array().min(1).max(30),
  pdf: joi
    .array()
    .optional()
    .items(
      joi.object({
        name: joi.string().required().max(50).min(1),
      }),
    ),
});

const TAB_FIELDS_SCHEMA: TabErrorSchemaTypes = {
  coreData: ["title", "body", "endDate"],
  pictures: ["image"],
  documents: ["pdf.*.name"],
};

export interface INewsItem {
  id?: number;
  title: string;
  body: EditorModel;
  startDate: number;
  endDate: number;
  image?: IImage | string;
  imageList: IFileState[];
  pictograms: string[];
  institution?: CompactInstitutionFragment;
  videos: Video[] | string[]; // overwrite videos type as we need to support string[] when Video exists on activity and Video[] when requesting an activity
  videoTabUrl: string; // URL input used to determine if should create a new Video resource in BE by comparing videos type.
  documents?: string[] | null | undefined;
  documentToUpload: IFileState[];
  category?: string;
  editRepeat: EditRepeatTypes;
  rRule?: RRule | RRuleSet;
  repeatId?: string;
  rrule: string;
  departments: string[];
  externalDepartments: string[];
  groups: string[];
  externalGroups: string[];
  parentGroups: string[];
  isCancelled: boolean;
  externalContentVisibility?: VisibleStateTypes;
  __typename: RRuleSupportedEntityType;
}

export interface NewsListFilters {
  categories: string[];
  groups: string[];
  departments: string[];
}

export interface INewsState extends IDefaultState {
  listFilters: NewsListFilters;
  item: INewsItem;
  pager: PagerManager<SummarizedNews>;
  selectedRruleOption: IOption;
  rruleToSave?: RruleOptions;
  listResourceType: ListResourceType;
}

export type NewsStore = Store<
  string,
  INewsState,
  IDefultStoreGetters<INewsState>,
  IDefaultStoreActions
>;

const DEFAULT_ITEM: INewsItem = {
  title: "",
  body: helpers.quill.getEmptyEditorModel(),
  startDate: getStartDate(),
  endDate: getEndDate(),
  image: undefined,
  imageList: [],
  pictograms: [],
  videos: [],
  videoTabUrl: "",
  documents: [],
  documentToUpload: [],
  category: undefined,
  editRepeat: "single",
  rrule: "",
  departments: [],
  externalDepartments: [],
  groups: [],
  externalGroups: [],
  parentGroups: [],
  isCancelled: false,
  externalContentVisibility: undefined,
  __typename: "SingleNews",
};

export const createUseStore = (id: StoreName) => {
  return defineStore(id, {
    state: (): INewsState => ({
      listFilters: {
        categories: [],
        groups: [],
        departments: [],
      },
      tabErrors: {},
      originalHash: null,
      validation: null,
      selectedRruleOption: REPEAT_RULE_OPTIONS[0]!,
      rruleToSave: undefined,
      item: {
        ...DEFAULT_ITEM,
      },
      pager: new PagerManager<SummarizedNews>(),
      listResourceType: "internal",
    }),
    getters: {},
    actions: {
      hasUnsavedChanges() {
        return !!(
          this.item &&
          this.originalHash &&
          this.originalHash !== this.currentHash()
        );
      },
      currentHash(): string {
        const data = {
          ...this.getItem(),
          imageList: this.item.imageList,
          documentToUpload: this.item.documentToUpload,
          selectedRruleOption: this.selectedRruleOption,
          rruleToSave: this.rruleToSave,
          externalContentVisibility: this.item.externalContentVisibility,
          videoTabUrl: this.item.videoTabUrl,
        };

        return md5(JSON.stringify(data));
      },
      setOriginalHash() {
        // It can take times to TextEditorInput convert html to delta.
        // That's why there is a timeout here.
        this.originalHash = this.currentHash();
      },
      unselectItem() {
        this.item = { ...DEFAULT_ITEM };
        this.setOriginalHash();
      },
      async setItem(id: IdType) {
        // No need to refetch
        if (id && this.item.id?.toString() === id.toString()) {
          return;
        }

        let data: NewsUnion | undefined;

        if (this.listResourceType === "internal") {
          data = await api.news.getNewsDetail(id);
        } else {
          data = await api.news.getExternalNewsDetail(id);
        }

        if (!data) {
          return;
        }

        this.item = {
          id: parseInt(data.id),
          title: data.title,
          startDate: toUnix(new Date(data.startDate)),
          endDate: toUnix(new Date(data.endDate)),
          body: helpers.quill.htmlToEditorModel(data.body || ""),
          imageList: toImageList(data),
          pictograms: (data.pictograms || [])?.map((item) => item.id),
          videoTabUrl: data.videos?.[0]?.url ?? "",
          documentToUpload: [],
          videos: [],
          rrule: "",
          category: data.category?.id || undefined,
          editRepeat: "single" as EditRepeatTypes,
          __typename: data.__typename as RRuleSupportedEntityType,
          // This is temporary. They will be set after the whenComposablesReady().
          departments: [],
          externalDepartments: [],
          groups: [],
          externalGroups: [],
          parentGroups: [],
          isCancelled: data.cancelled,
          institution: data.institution,
          externalContentVisibility: undefined,
        };

        // We should wait the list to be loaded to select departments. Because API
        // returns departments and external departments in the same array. We don't
        // know which id is related with which list. That causes a race condition
        // issue.
        const departments = useDepartments();
        const externalDepartments = useExternalDepartments(Module_Type.News);
        const groups = useGroups(Module_Type.News);
        const externalGroups = useExternalGroups(Group_Connection.News);
        let entityVisibility = undefined;

        if (this.listResourceType === "external") {
          entityVisibility = useEntityVisibility(Module_Type.News);
          // No need to await here. whenComposablesReady() function will handle it
          entityVisibility.load();
        }

        // We don't know when the composable data will be loaded. But we can not
        // wait to select an item because it effects lots of things such as
        // spinners, warnings, etc. Because of the race condition issue, setting
        // the following data should be wait a little. This will be occurred only
        // when the user refresh the page on activities. Once the data is ready,
        // users can't notice any difference.
        whenComposablesReady(
          [
            departments,
            externalDepartments,
            groups,
            externalDepartments,
            entityVisibility,
          ],
          () => {
            this.item.departments = converters.shared.toSelectableIds(
              departments.departmentList,
              data.departments,
            );

            this.item.externalDepartments = converters.shared.toSelectableIds(
              externalDepartments.externalDepartmentList,
              data.departments,
            );

            this.item.groups = converters.shared.toSelectableIds(
              groups.groupList,
              data.groups,
            );
            this.item.externalGroups = converters.shared.toSelectableIds(
              externalGroups.externalGroupList,
              data.groups,
            );
            this.item.parentGroups = converters.shared.toSelectableIds(
              groups.parentGroupList,
              data.groups,
            );

            if (entityVisibility) {
              this.item.externalContentVisibility = entityVisibility.isVisible(
                this.item.id,
              );
            }

            this.setOriginalHash();
          },
        );

        // Setting the rrule data
        if (data.__typename === "RepeatingNews" && data.repeat) {
          const rule = rrulestr(data.repeat.rrule);
          this.rruleToSave = fromRRuletoRuleState(rule);
          this.item.rRule = rule;
          this.item.repeatId = data.repeat.id;
        }

        if (data.documents && data.documents.length > 0) {
          const [document] = data.documents;
          if (document?.url.includes(".pdf")) {
            this.item.documentToUpload = [
              {
                id: document.id,
                name: document.title,
                url: document.url,
                base64: "",
                type: "pdf",
              },
            ];
          }
        }

        this.setOriginalHash();
        this.validation = null;
      },
      getItem(): NewsInput {
        const item = { ...this.item };
        const authStore = useAuthStore();

        const id = item?.id?.toString();

        let image = item.image;
        if (typeof image === "object") {
          image = image.id;
        }

        let rruleString;
        let isRepeat = false;
        let repeat;
        // Setting the repeat rule for repeating activities
        if (this.rruleToSave) {
          const rule = toRRule(this.rruleToSave);
          rruleString = rule.toString();
          isRepeat = true;
          repeat = item.repeatId;
        }

        // We shouldn't marked as repeat on updates if it's not selected a repeat
        if (id && this.item.editRepeat === "single") {
          isRepeat = false;
          repeat = null;
          rruleString = null;
        }

        const { departments, groups } = converters.shared.toSharedOptions(item);

        return {
          description: helpers.quill.editorModelToHtml(item.body),
          endDate: fromUnixToAPIDate(item.endDate),
          id: item?.id?.toString(),
          institution:
            item.institution?.id?.toString() || authStore.institutionId,
          startDate: fromUnixToAPIDate(item.startDate),
          status: item.isCancelled ? 2 : 1,
          title: item.title,
          image: image as string | null | undefined,
          pictograms: item.pictograms,
          documents: item.documents,
          category: item.category || null,
          isRepeat,
          rrule: rruleString,
          repeat,
          departments,
          groups,
        };
      },
      async isNotValid() {
        this.validation = validate(VALIDATION_SCHEMA, {
          ...this.item,
          description: helpers.quill.editorModelToText(this.item.body),
          image: this.item.imageList.filter((item) => item.action !== "delete"),
          pdf: IS_IBG_MANAGER
            ? this.item.documentToUpload.filter(
                (item) => item.action !== "delete",
              )
            : [],
        });

        // We can't use usei18n() in stores.
        // https://stackoverflow.com/a/71449708
        const { t } = globali18n;

        // We should fix the validation labels
        fixValidationFields(this.validation, {
          startDate: t("global.visible_from"),
        });

        this.tabErrors = helpers.forms.getTabErrors(
          TAB_FIELDS_SCHEMA,
          this.validation.errors,
        );

        return !this.validation.isValid;
      },
      async fetchItems(filter: DefaultFilterType) {
        if (filter === "show_external_content") {
          // We should load some data here
          useEntityVisibility(Module_Type.News);

          const { dateFrom, dateTo } = toExternalDateRange();
          this.listResourceType = "external";

          const params: EntityPagerFilter = {
            dateFrom,
            dateTo,
            sorting: {
              field: Sort_Field.StartDate,
              order: Sort_Order.Asc,
            },
          };
          await this.pager.paginate(api.news.getExtarnalNews, params, this);
        } else {
          this.listResourceType = "internal";
          const { dateFrom, dateTo } = toLongDateRange(
            filter === "show_upcoming",
          );

          const params: EntityPagerFilter = {
            dateFrom,
            dateTo,
            sorting: {
              field: Sort_Field.StartDate,
              order:
                filter === "show_upcoming" ? Sort_Order.Asc : Sort_Order.Desc,
            },
          };

          await this.pager.paginate(api.news.getNews, params, this);
        }
      },
    },
  });
};

// News View store
export const useNewsStore = createUseStore("NEWS");

// We use the same store structure for the create modal.
export const useAddNewsStore = createUseStore("ADD_NEWS");
